import { useEffect } from 'react';
import { HttpStatusCode } from 'axios';
import { isRouteErrorResponse, useRouteError } from '@remix-run/react';

import { useDialog } from '@/contexts/DialogContext';

// 🥲, just don't wanna to import from `@/analytics/{project}` cus it's sharable place
import { _sendEventToAnalytics as sendEventToAnalytics } from '@/utils/analytics/analytics';
import { ClientError } from '@/utils/clientError';

import {
  SharedHeader,
  links as sharedHeaderLinks,
} from '@/components/shared/shared-header/shared-header';
import {
  SharedFooter,
  links as sharedFooterLinks,
} from '@/components/shared/shared-footer/shared-footer';
import {
  HackerViewBenefits,
  links as hackerViewBenefitsLinks,
} from '@/components/hacker-view/hacker-view-benefits/hacker-view-benefits';
import {
  SharedModal,
  links as sharedModalLinks,
} from '@/components/shared/shared-modal/shared-modal';
import {
  HackerViewButton,
  links as hackerViewButtonLinks,
} from '@/components/hacker-view/hacker-view-button/hacker-view-button';

import { ApiError } from '@/types/errors';
import type { LinksFunction } from '@remix-run/node';
import {
  SHARABLE_EVENT_TYPE as EVENT_TYPE,
  SHARABLE_UI_VIEW_EVENT as UI_VIEW_EVENT,
} from '@/types/analytics';
import { DialogName } from '@/types/hacker-view/dialogs';

import styles from './error-boundary-template.css?url';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  ...sharedHeaderLinks(),
  ...sharedFooterLinks(),
  ...hackerViewBenefitsLinks(),
  ...sharedModalLinks(),
  ...hackerViewButtonLinks(),
];

export const ErrorBoundaryTemplate = () => {
  const error = useRouteError();
  const { openDialog, closeDialog, activeDialog } = useDialog();

  const isDefaultClientError = error instanceof Error;
  const isOwnClientError = error instanceof ClientError;
  const isRouteError = isRouteErrorResponse(error);

  const isHasAdditionalData = isRouteError || isOwnClientError;
  const notifyMessage = isHasAdditionalData
    ? error.data.notifyMessage || ApiError.REQUEST_FAILED
    : ApiError.DEFAULT;
  const description = (() => {
    if (isRouteError || isOwnClientError) {
      if (typeof error.data === 'string') {
        return error.data || notifyMessage;
      }
      return error.data.description || notifyMessage;
    }
    if (error instanceof Error) {
      return error.message;
    }
  })();

  const handleAnalyticsErrorMessage = () => {
    if (isHasAdditionalData) {
      return {
        error_status: error.status,
        error_status_text: error.statusText,
        ...error.data,
      };
    }

    if (isDefaultClientError) {
      return {
        error_status: HttpStatusCode.ExpectationFailed,
        error_status_text: 'Expectation Failed',
        message: error.toString(),
        isClientError: true,
      };
    }

    return error;
  };

  useEffect(() => {
    setTimeout(() => {
      sendEventToAnalytics(
        EVENT_TYPE.UIView,
        UI_VIEW_EVENT.ERROR,
        handleAnalyticsErrorMessage(),
      );
    }, 500);

    openDialog(DialogName.ERROR_MESSAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="error-boundary-template">
      <SharedHeader />

      <div className="container error-boundary-template__wrapper">
        <h1 className="error-boundary-template__title">Something went wrong</h1>
        <p className="error-boundary-template__description">{description}</p>
      </div>

      <HackerViewBenefits
        withVideo
        title="Hackers can target anyone"
        description="Aura helps reduce your exposure to scams and fraud with continuous privacy protection, so you can rest easy."
        className="error-boundary-template__benefits"
      />

      <SharedFooter theme="light" withDisclaimer />

      <SharedModal
        onClose={closeDialog}
        isOpen={activeDialog === DialogName.ERROR_MESSAGE}
        title={notifyMessage}
      >
        <HackerViewButton
          intent="primary"
          onClick={closeDialog}
          className="error-boundary-template__error-button"
        >
          OK
        </HackerViewButton>
      </SharedModal>
    </div>
  );
};
